import React, { useState } from 'react'

import { useHistory } from 'react-router-dom'
import { Box, Button } from '@material-ui/core'
import { FormContext, useFieldArray, useForm } from 'react-hook-form'
import { reverse } from 'named-urls'

import { Card, LoadingFeedback } from 'components'

import {
  NcActionsForm,
  NcAttachmentForm,
  NcGeneralFields,
  NcSpecificFields,
} from './components'

import { routes } from 'Routes'
import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

const NcForm = ({ monitoringManagementNc, refresh, isShow = false }) => {
  const [isLoading, setIsLoading] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()

  const { response: responseDepartments, isLoading: isDepartmentsLoading } =
    useFetch(service.dponet.departments.get, {
      perPage: 1000,
      order: 'name ASC',
      minimal: true,
    })

  const formMethods = useForm({
    validationSchema: schema(),
    defaultValues: {
      title: monitoringManagementNc?.title || '',
      responsibleId: monitoringManagementNc?.responsible || null,
      priority: monitoringManagementNc?.priority || '',
      departmentId: monitoringManagementNc?.department?.id || '',
      description: monitoringManagementNc?.description || '',
      objective: monitoringManagementNc?.objective || '',
      ncOrigin: monitoringManagementNc?.ncOrigin || '',
      occurrenceDate: monitoringManagementNc?.occurrenceDate || null,
      classification: monitoringManagementNc?.classification || '',
      rootCauseAnalysis: monitoringManagementNc?.rootCauseAnalysis || '',
      attachments: monitoringManagementNc?.attachments || null,
      ncActions:
        monitoringManagementNc?.ncActions?.map((action) => ({
          originId: action.id,
          correctiveAction: action.correctiveAction || '',
          responsibleId: action.responsible || null,
          deadline: action.deadline || null,
          immediateAction: action.immediateAction || '',
          preventiveAction: action.preventiveAction || '',
          effectivenessVerification: action.effectivenessVerification || '',
          verificationDate: action.verificationDate || null,
          provenEffectiveness: action.provenEffectiveness || false,
        })) || [],
    },
  })

  const { control, errors, handleSubmit } = formMethods
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ncActions',
  })

  const departments = responseDepartments?.data?.departments || []

  const responseUsers = async (inputValue) => {
    try {
      const response = await service.dponet.users.get({
        name: inputValue,
        perPage: 10000000,
        minimal: true,
        order: 'name ASC',
      })
      return response?.data?.users || []
    } catch {
      return []
    }
  }

  const { NC_ORIGIN_OPTIONS, CLASSIFICATION_OPTIONS, PRIORITY_OPTIONS } =
    constants.monitoringManagementNc

  const isEdit = !!monitoringManagementNc?.id

  const buildNcActionsPayload = (ncActions) =>
    (ncActions || []).map((action) => ({
      ...(action.originId && { id: action.originId }),
      correctiveAction: action.correctiveAction,
      responsibleId: action.responsibleId?.id,
      deadline: helpers.formatters.toISOStringVerify(action.deadline),
      immediateAction: action.immediateAction,
      preventiveAction: action.preventiveAction,
      effectivenessVerification: action.effectivenessVerification,
      verificationDate: helpers.formatters.toISOStringVerify(
        action.verificationDate,
      ),
      provenEffectiveness: action.provenEffectiveness,
    }))

  const onSubmit = async (data) => {
    setIsLoading(true)

    try {
      const occurrenceDate = helpers.formatters.toISOStringVerify(
        data?.occurrenceDate,
      )
      const ncActions = buildNcActionsPayload(data.ncActions)

      if (!isEdit) {
        const { attachments: _attachments, ...dataWithoutAttachments } = data

        const response = await service.dponet.monitoringManagementNc.create({
          ...dataWithoutAttachments,
          responsibleId: data?.responsibleId?.id,
          occurrenceDate,
          ncActions,
        })

        const createdNc = response?.data?.monitoringManagementNc

        const newFiles = data.attachments
          ? Array.from(data.attachments).filter((f) => f instanceof File)
          : []

        if (newFiles.length > 0) {
          await service.dponet.monitoringManagementNc.uploadAttachments({
            monitoringManagementNcId: createdNc?.id,
            attachments: newFiles,
          })
        }

        snackbar.open({
          message: 'Não Conformidade criada com sucesso.',
          variant: 'success',
        })

        history.push(
          reverse(routes.nc.edit, {
            monitoringManagementNcId: createdNc?.id,
          }),
        )
      }

      if (isEdit) {
        const { attachments: _attachments, ...dataWithoutAttachments } = data

        await service.dponet.monitoringManagementNc.update({
          monitoringManagementNcId: monitoringManagementNc?.id,
          ...dataWithoutAttachments,
          responsibleId: data?.responsibleId?.id,
          occurrenceDate,
          ncActions,
        })

        const newFiles = data.attachments
          ? Array.from(data.attachments).filter((f) => f instanceof File)
          : []

        if (newFiles.length > 0) {
          await service.dponet.monitoringManagementNc.uploadAttachments({
            monitoringManagementNcId: monitoringManagementNc?.id,
            attachments: newFiles,
          })
        }

        snackbar.open({
          message: 'Não Conformidade atualizada com sucesso.',
          variant: 'success',
        })
        refresh()
      }
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao salvar a Não Conformidade.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={isLoading || isDepartmentsLoading} />
      <FormContext {...formMethods}>
        <form id="nc-form" onSubmit={handleSubmit(onSubmit)}>
          <Card title="Informações">
            <NcGeneralFields
              control={control}
              errors={errors}
              isShow={isShow}
              departments={departments}
              priorityOptions={PRIORITY_OPTIONS}
              responseUsers={responseUsers}
            />

            <NcSpecificFields
              control={control}
              errors={errors}
              isShow={isShow}
              ncOriginOptions={NC_ORIGIN_OPTIONS}
              classificationOptions={CLASSIFICATION_OPTIONS}
            />
          </Card>
        </form>

        <NcAttachmentForm
          monitoringManagementNc={monitoringManagementNc}
          refresh={refresh}
          setLoading={setIsLoading}
          isDisabled={isShow}
        />

        <NcActionsForm
          monitoringManagementNcId={monitoringManagementNc?.id}
          fields={fields}
          append={append}
          remove={remove}
          isDisabled={isShow}
          responseUsers={responseUsers}
          refresh={refresh}
          setLoading={setIsLoading}
        />
      </FormContext>

      <Box ml={2} mt={2} display="flex" justifyContent="flex-end">
        <Box pr={1}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => history.push(routes.nc.all)}
          >
            {isEdit ? 'Voltar' : 'Cancelar'}
          </Button>
        </Box>
        {!isShow && (
          <Box>
            <Button
              form="nc-form"
              type="submit"
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

export default NcForm
